/**
 * Modèles exposés par l'API (vue joueur).
 *
 * Règle de nommage (DECISIONS §3) : JSON d'API en `camelCase`. Les mots du domaine restent en
 * français (`niveauMax`, `eclats`, `eclatsRun`, `niveauAtteint`), les noms techniques en anglais.
 * Aucun champ de ces modèles n'est calculé par le client : `eclats` et `pointsCollection` sont
 * produits par le serveur et **jamais** acceptés en entrée (DECISIONS §8).
 */
import { z } from 'zod';
import { LOCALES, PSEUDO_ANONYMISE, PSEUDO_MAX_LENGTH, PSEUDO_MIN_LENGTH, STATUTS_RUN, STATUTS_VAGUE, } from '../constants.js';
import { modeSchema } from '../content/schemas.js';
/** Locale de l'interface. */
export const localeSchema = z.enum(LOCALES);
/** Rôle du compte. */
export const userRoleSchema = z.enum(['joueur', 'admin']);
/** Mode de jeu — schéma défini par le contenu, réexporté pour l'API. */
export { modeSchema } from '../content/schemas.js';
/** Rareté d'une carte — schéma et type définis par le contenu, réexportés pour l'API. */
export { rareteSchema } from '../content/schemas.js';
/** Statut d'une run. */
export const runStatusSchema = z.enum(STATUTS_RUN);
/** Statut d'une vague dans une run. */
export const vagueStatusSchema = z.enum(STATUTS_VAGUE);
/** Pseudo : identifiant public du joueur. */
export const pseudoSchema = z
    .string()
    .trim()
    .min(PSEUDO_MIN_LENGTH, `le pseudo doit contenir au moins ${PSEUDO_MIN_LENGTH} caractères`)
    .max(PSEUDO_MAX_LENGTH, `le pseudo ne peut pas dépasser ${PSEUDO_MAX_LENGTH} caractères`)
    .regex(/^[A-Za-z0-9_-]+$/, 'le pseudo accepte lettres, chiffres, tiret et tiret bas uniquement')
    // `ancien-joueur` est le pseudo des comptes anonymisés (§11) : le laisser choisir
    // empêcherait l'anonymisation d'un autre compte (unicité partielle des pseudos actifs).
    .refine((valeur) => valeur !== PSEUDO_ANONYMISE, `le pseudo « ${PSEUDO_ANONYMISE} » est réservé aux comptes supprimés`);
/**
 * Email **normalisé en minuscules** (DECISIONS §4.2).
 *
 * La normalisation appartient au schéma : le serveur ne stocke jamais une casse différente,
 * et `EMAail_ALREADY_USED` ne peut pas être contourné par `Pseudo@Example.com`.
 */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.string().email("l'adresse email n'est pas valide").max(320));
/** Compte joueur, tel que renvoyé par l'API. Ne contient jamais `passwordHash`. */
export const userSchema = z
    .object({
    id: z.string().uuid(),
    email: z.string(),
    pseudo: z.string(),
    locale: localeSchema,
    role: userRoleSchema,
    emailVerified: z.boolean(),
    createdAt: z.string().datetime(),
})
    .strict();
/** Progression persistante du joueur (recalculée côté serveur, jamais fournie par le client). */
export const progressionSchema = z
    .object({
    userId: z.string().uuid(),
    /** Plus haut niveau atteint en mode limité. */
    niveauMax: z.number().int().nonnegative(),
    /** Solde d'éclats validés (dépensables en améliorations permanentes). */
    eclats: z.number().int().nonnegative(),
    /** Points de collection cumulés — **non dépensables** (F15). */
    pointsCollection: z.number().int().nonnegative(),
    updatedAt: z.string().datetime(),
})
    .strict();
/** Améliorations permanentes achetées : `{ upgradeId: niveau }`. */
export const ameliorationsPermanentesSchema = z.record(z.string(), z.number().int().nonnegative());
/** Tour posée pendant une run (état d'autorité serveur). */
export const tourPoseeSchema = z
    .object({
    /** Identifiant d'instance, unique dans la run. */
    id: z.string().uuid(),
    towerId: z.string(),
    emplacementId: z.string(),
    /** Niveau courant de la tour (1 à 3). */
    niveau: z.number().int().positive(),
    /** `true` si la pose a consommé un joker (pose pendant une vague, F06). */
    poseParJoker: z.boolean(),
})
    .strict();
/**
 * Multiplicateurs consolidés des cartes obtenues pendant une run (DECISIONS §6.8, F16).
 *
 * **Sémantique imposée** : ce sont des **sommes de pourcentages**, jamais des facteurs
 * multiplicatifs. La formule unique du serveur est
 * `valeurEffective = valeurBase × (1 + Σ / 100)` : `+10 %` puis `+15 %` valent `+25 %`
 * (et non `× 1,265`). Le cumul est **sans plafond** (F16) : aucun champ n'est borné.
 *
 * - `parStat` : cumul de tous les bonus obtenus pour chaque statistique de tour, plus les
 *   deux coûts (`coutTour`, `coutAmelioration`, qui additionnent les `bonusStat` et les
 *   `bonusEconomie` de même cible). C'est la vue « ce que la run a rapporté ».
 * - `gainEclats` : pourcentage additif de gain d'éclats (cartes uniquement).
 * - `parElement` : pour une tour de cet élément, le cumul **réellement applicable**
 *   (bonus non filtrés + bonus filtrés sur cet élément). C'est la valeur à utiliser pour
 *   une tour précise ; `parStat` ne doit pas être additionné à `parElement`.
 *
 * Ces valeurs sont **sortantes uniquement** : aucune route n'en accepte en entrée, et le
 * client ne les recalcule jamais lui-même (DECISIONS §8).
 */
const multiplicateursParStatSchema = z
    .object({
    degats: z.number(),
    portee: z.number(),
    cadenceMs: z.number(),
    vitesseProjectile: z.number(),
    coutTour: z.number(),
    coutAmelioration: z.number(),
})
    .strict();
const multiplicateursParElementSchema = z
    .object({
    degats: z.number(),
    portee: z.number(),
    cadenceMs: z.number(),
    vitesseProjectile: z.number(),
})
    .strict();
export const multiplicateursRunSchema = z
    .object({
    parStat: multiplicateursParStatSchema,
    gainEclats: z.number(),
    /** Clé = identifiant d'élément du contenu (`feu`, `eau`, `terre`, `air`). */
    parElement: z.record(z.string(), multiplicateursParElementSchema),
})
    .strict();
/**
 * Multiplicateurs d'une run **sans aucune carte obtenue** : tout à zéro, aucun élément.
 *
 * Utilisé par le serveur à la création de la run, et disponible pour les clients et les
 * tests qui ont besoin d'un état valide au regard du schéma (les champs sont obligatoires).
 */
export function multiplicateursNeutres() {
    return {
        parStat: {
            degats: 0,
            portee: 0,
            cadenceMs: 0,
            vitesseProjectile: 0,
            coutTour: 0,
            coutAmelioration: 0,
        },
        gainEclats: 0,
        parElement: {},
    };
}
/**
 * État d'autorité d'une run, renvoyé par `POST /runs` et `GET /runs/:runId`
 * (DECISIONS §4.2). C'est la **seule** source de vérité du solde : le client l'affiche, il ne
 * le calcule pas.
 */
export const runStateSchema = z
    .object({
    levelId: z.string(),
    mode: modeSchema,
    /** Éclats disponibles **dans la run** (dépensables en tours et améliorations de tour). */
    eclatsRun: z.number().int().nonnegative(),
    /** Éclats au démarrage de la run (information d'affichage). */
    eclatsDepart: z.number().int().nonnegative(),
    jokers: z.number().int().nonnegative(),
    /** Cartes possédées pendant la run (deck de la run), dédupliquées. */
    deck: z.array(z.string()),
    /** Améliorations permanentes actives, appliquées au départ de la run. */
    ameliorations: ameliorationsPermanentesSchema,
    contentVersion: z.string(),
    /** Graine de simulation, sérialisée en chaîne (entier 64 bits). */
    seed: z.string(),
    /** PV restants du parcours — copie serveur, jamais modifiable par le client. */
    pvParcours: z.number().int().nonnegative(),
    /**
     * Cumul des effets des cartes obtenues pendant la run (DECISIONS §6.8, F16).
     * **Obligatoire** : le serveur le publie à chaque réponse, le client ne le recalcule pas.
     */
    multiplicateurs: multiplicateursRunSchema,
})
    .strict();
/** Résumé de run pour l'historique (`GET /me/runs`). */
export const runResumeSchema = z
    .object({
    id: z.string().uuid(),
    mode: modeSchema,
    levelId: z.string(),
    status: runStatusSchema,
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    niveauAtteint: z.number().int().nonnegative(),
    eclatsValides: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=models.js.map